import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { NoteFullScreenComponent } from '@src/suite/common/st-full-screen-dialog/note-full-screen/note-full-screen';
import { FullscreenActions, selectFullscreenRecordGuid, selectParentRecord, SidecarEfffectActions, SidecarMenu } from '@src/suite/common/store/fullscreen';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { NotesRecordActions, RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { v4 } from 'uuid';

import { ModalService } from '../../components/modal/modal.service';
import { ViewStateFilterSerializer } from '../../data-models/serializers/serializers';
import { ObjectTypes } from '../../enums/object-types';
import { BasicContentService } from '../../services/basic-content-service';
import { GlobalTablesService } from '../../services/global-tables-service';
import { NomWebServicesJson } from '../../services/nom-web-services-json';
import { initializeRecord } from '../../utils/record-utils';
import { extractNoteBeginText } from '../../utils/util';
import { selectCurrentTeam } from '../navigation/selectors/navigation.selectors';
import { NotesActions, NotesApiActions } from './notes.actions';
import { selectNotesFilters, selectNotesSearchTerm, selectNotesSort } from './notes.selectors';

@Injectable()
export class NotesEffects {
    private actions$: Actions = inject(Actions);
    private dialog = inject(MatDialog);
    private store: Store = inject(Store);
    private playbookSearchService = inject(PlaybookSearchService);
    private basicContentService = inject(BasicContentService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private modalService = inject(ModalService);
    private playbookService = inject(PlaybookService);
    private globalTablesService = inject(GlobalTablesService);

    private dialogRef: MatDialogRef<NoteFullScreenComponent, unknown> = null;

    onOpenNoteEditor$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NotesActions.openCreateNewNote, NotesActions.openEditNote, NotesActions.openCreateNoteTemplate),
            switchMap(action => {
                if (this.dialogRef) this.dialogRef.close();
                this.dialogRef = this.dialog.open(NoteFullScreenComponent, {
                    hasBackdrop: true,
                    disableClose: false,
                    autoFocus: true,
                    width: '60vw',
                    height: '90vh',
                    panelClass: 'st-note-full-screen-panel',
                    data: {
                        createNoteTemplate: action.type === NotesActions.openCreateNoteTemplate.type,
                    },
                });
                return this.dialogRef.afterClosed().pipe(
                    switchMap(() => {
                        return [NotesActions.resetNewNoteState(), NotesActions.closeEditNote()];
                    })
                );
            })
        );
    });

    updateNote$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NotesActions.updateCurrentNote),
            map(({ fieldsToUpdate, record }) => {
                if (record.isNewRecord) {
                    return NotesApiActions.createNote({ note: record });
                }
                return RecordHttpActions.saveRecordChanges({ record, fieldsToUpdate });
            })
        );
    });

    initializeNotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.selectSidecarMenu),
            filter(action => action.menu === SidecarMenu.NOTES),
            concatLatestFrom(() => [this.store.select(selectFullscreenRecordGuid)]),
            switchMap(([_, fullscreenRecordGuid]) => {
                const notesFilter = ViewStateFilterSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.NOTE);
                return of(NotesActions.initializeNotesFilter({ recordGuid: fullscreenRecordGuid, filter: notesFilter }));
            })
        );
    });

    setNotes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openTopic, NotesActions.initializeNotesFilter, NotesActions.updateNotesFilter, NotesActions.searchNotes, NotesActions.changeSearchTerm),
            concatLatestFrom(() => [this.store.select(selectNotesFilters), this.store.select(selectNotesSearchTerm), this.store.select(selectNotesSort), this.store.select(selectParentRecord)]),
            switchMap(([_, notesFilters, searchTerm, sort, parentRecord]) => {
                return this.playbookSearchService.getNotesResultsObservable(parentRecord?.Guid, searchTerm, notesFilters, sort).pipe(
                    switchMap(response => {
                        const actions = [];
                        actions.push(NotesRecordActions.setNotes({ records: response.data.notes }));
                        return actions;
                    }),
                    catchError(error => {
                        console.warn('Error fetching notes:', error);
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    setNoteTemplateID$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.openNewTemplateDialog),
            filter(action => action.record.Type === ObjectTypes.NOTE),
            map(action => {
                return NotesActions.selectRecordTemplateGuid({ templateGuid: action.record.Guid });
            })
        );
    });

    saveNoteTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NotesActions.saveNoteTemplate),
            switchMap(({ record }) => {
                const currentTeam = this.store.selectSignal(selectCurrentTeam);
                const data = {
                    CreateTime: new Date().toJSON(),
                    Type: ObjectTypes.NOTE,
                    RawContent: record.RawContent,
                    PSTNoteStatus: record.PSTNoteStatus,
                };
                let fieldsToUpdate: { FieldName: string; FieldValue: unknown }[] = [];
                fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(data);
                fieldsToUpdate.forEach(field => {
                    field.FieldValue = this.nomWebServicesJson.convertor.serializeField(field.FieldValue, field.FieldName, ObjectTypes.SCRIPT)?.value || field.FieldValue;
                });
                return this.basicContentService
                    .createNoteTemplate({
                        database: record.Database,
                        path: `P_${currentTeam()}\\X`,
                        fieldsToUpdate: fieldsToUpdate,
                    })
                    .pipe(
                        switchMap(response => {
                            const noteContent = extractNoteBeginText(record.RawContent);
                            this.modalService.openAlert({ title: 'Template Created', content: `You have successfully saved ${noteContent} as note template.` });
                            const actions = [];
                            actions.push(NotesRecordActions.updateNoteTemplates({ templates: [response.data] }));
                            return actions;
                        }),
                        catchError(error => {
                            console.warn('Error creating note template:', error);
                            return of(ApplicationActions.noOpAction());
                        })
                    );
            })
        );
    });

    initNoteRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NotesActions.initializeNewNote),
            switchMap(async action => {
                let newRecord = null;
                if (action.templateRecord) {
                    newRecord = await this.playbookService.initializeNoteFromTemplate(action.templateRecord.Database, action.templateRecord.Path.fullPath, action.templateRecord.Guid);
                } else {
                    newRecord = initializeRecord<StorytellingNoteRecord>(ObjectTypes.NOTE);
                }
                newRecord.isNewRecord = true;
                newRecord.Guid = v4();
                newRecord.PSTAncestorGUID = action.parentRecordGuid;
                newRecord.PSTNoteStatus = action?.templateRecord ? action.templateRecord.PSTNoteStatus : { id: '', title: '' };
                newRecord.Title = '';
                newRecord.RawContent = action?.templateRecord ? action.templateRecord.RawContent : '';
                return NotesActions.openCreateNewNote({ newRecord: newRecord });
            })
        );
    });
}
